import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { Check, CheckCheck, Plus, Sparkles, X } from 'lucide-react';
import { CommandList } from 'cmdk';
import { getModelsForInbox } from 'src/features/messages/meta-panel/ModelPanel';
import TagCrudDialog from 'src/features/models/[modelId]/components/TagCrudDialog';
import { useModels } from 'src/features/models/queries/useModels';
import { getIconForModel, getModelMap, sortMessageModels } from 'src/shared/lib/model.utils';
import { ActualsUpdateType, MessageType } from 'src/features/messages/types/message.types';
import DynamicIcon from 'src/shared/components/ui/icons/dynamic-icon';
import { cn, uniqueArray } from 'src/shared/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from 'src/shared/components/ui/popover';
import TagHierarchy from 'src/shared/components/typography/TagHierarchy';
import WithTooltip from 'src/shared/components/utils/WithTooltip';
import { useMutateMessage } from 'src/features/messages/queries/useMutateMessage';
import { useAuth } from 'src/features/auth/contexts/AuthContext';
import mixpanel from 'src/shared/lib/mixpanel';
import { Tag } from 'src/shared/types/model.types';
import {
  Command,
  CommandInput,
  CommandItem,
  CommandEmpty,
  CommandHeading,
  CommandGroup,
} from 'src/shared/components/ui/command';
import WithAccessControl from 'src/shared/components/utils/WithAccessControl';

type RenderModelPopoverProps = {
  model: { id: string; tags: Tag[]; displayName: string };
  currentTagId?: string;
  searchValue: string;
  canCreateNewTag?: boolean;
  onChangeTag: (modelId: string, currentTagId: string | null, newInternalId: string, action: ActualsUpdateType) => void;
  setSearchValue: (value: string) => void;
  setTagCrudOpen: () => void;
  setInitialTagName: (name: string) => void;
};

function RenderModelPopover({
  model,
  currentTagId,
  searchValue,
  canCreateNewTag,
  onChangeTag,
  setSearchValue,
  setTagCrudOpen,
}: RenderModelPopoverProps) {
  const filteredTags = useMemo(() => {
    try {
      if (!model || !model.tags || !Array.isArray(model.tags) || model.tags.length === 0) {
        return [];
      }

      return uniqueArray(model.tags, 'internalId')
        .sort((a, b) => a.hierarchy.join('').localeCompare(b.hierarchy.join('')))
        .filter((tag) => !searchValue || tag.hierarchy.join('').toLowerCase().includes(searchValue.toLowerCase()));
    } catch (error) {
      console.error('Error filtering tags', error);
      return [];
    }
  }, [model, searchValue]);

  return (
    <>
      <PopoverContent onClick={(e) => e.stopPropagation()} align="start" className="w-80 p-0 overflow-hidden">
        <Command className="rounded-none border-none shadow-none">
          <CommandHeading>Tags for {model.displayName}</CommandHeading>
          <WithAccessControl blockReaders>
            <CommandInput
              placeholder="Search tags..."
              value={searchValue}
              onValueChange={setSearchValue}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && searchValue && filteredTags.length === 0) {
                  e.preventDefault();
                  setTagCrudOpen();
                }
              }}
              className="h-8"
            />
          </WithAccessControl>
          <CommandList>
            <CommandGroup className="p-1 max-h-64 overflow-y-auto">
              {filteredTags.length > 0 ? (
                filteredTags.map((tag) => (
                  <CommandItem
                    key={tag.id}
                    onSelect={() =>
                      onChangeTag(
                        model.id,
                        currentTagId ?? null,
                        tag.internalId,
                        currentTagId ? (currentTagId !== tag.internalId ? 'UPDATE' : 'VERIFY') : 'ADD'
                      )
                    }
                    className={cn(
                      'flex items-center justify-between py-1.5 px-2 text-sm text-neutral-500 hover:bg-neutral-100 cursor-pointer rounded-sm',
                      currentTagId === tag.internalId && 'bg-neutral-100 text-typography-black'
                    )}
                  >
                    <TagHierarchy hierarchy={tag.hierarchy} className="min-w-0 flex-1" />
                    {currentTagId === tag.internalId && (
                      <Check size={14} className="shrink-0 ml-2 text-typography-black" />
                    )}
                  </CommandItem>
                ))
              ) : (
                <WithAccessControl blockReaders>
                  <CommandEmpty className="p-0 text-sm text-neutral-500">
                    <div
                      onClick={() => setTagCrudOpen()}
                      className="flex items-center gap-2 py-1.5 px-2 text-sm text-neutral-500 hover:bg-neutral-100 cursor-pointer rounded-sm"
                    >
                      <Plus size={14} className="shrink-0" />
                      Create {'"'}
                      {searchValue}
                      {'"'}
                    </div>
                  </CommandEmpty>
                </WithAccessControl>
              )}
            </CommandGroup>
          </CommandList>
          {canCreateNewTag && (
            <WithAccessControl blockReaders>
              <div className="p-1 border-t">
                <div
                  onClick={() => setTagCrudOpen()}
                  className="flex items-center gap-2 py-1.5 px-2 text-sm text-neutral-500 hover:bg-neutral-100 cursor-pointer rounded-sm"
                >
                  <Plus size={14} className="shrink-0" />
                  Create tag
                </div>
              </div>
            </WithAccessControl>
          )}
        </Command>
      </PopoverContent>
    </>
  );
}

type ModelBadgesProps = {
  integrationId: string;
  threadId: string;
  modelOutput: MessageType['models'];
  facetsVersions: MessageType['facets_versions'];
  compact?: boolean;
  className?: string;
  canCreateNewTag?: boolean;
  onOpenChange?: (open: boolean) => void;
  onTagChange?: (action: ActualsUpdateType) => void;
};

const ModelBadges = memo(
  function ModelBadges({
    integrationId,
    threadId,
    modelOutput = {},
    facetsVersions = {},
    compact = false,
    className,
    canCreateNewTag = true,
    onOpenChange = () => {},
    onTagChange = () => {},
  }: ModelBadgesProps) {
    const { user } = useAuth();
    const { data: models } = useModels();
    const { updateMessage } = useMutateMessage();
    const [openModelId, setOpenModelId] = useState<string | null>(null);
    const [openPopoverKey, setOpenPopoverKey] = useState<string | null>(null);
    const [searchValue, setSearchValue] = useState('');
    const [tagCrudOpen, setTagCrudOpen] = useState(false);
    const [initialTagName, setInitialTagName] = useState('');
    const onOpenChangeRef = useRef(onOpenChange);

    useEffect(() => {
      if (openPopoverKey !== null || openModelId !== null) {
        onOpenChangeRef.current(true);
      }
    }, [openPopoverKey, openModelId]);

    const modelMap = useMemo(() => getModelMap(models || []), [models]);
    const modelsForInbox = useMemo(() => getModelsForInbox(models || [], integrationId), [models, integrationId]);

    function handleVerify(modelId: string, tagInternalId: string) {
      handleChange(modelId, tagInternalId, tagInternalId, 'VERIFY');
    }

    function handleChange(
      modelId: string,
      oldInternalId: string | null,
      newInternalId: string | null,
      action: ActualsUpdateType
    ) {
      const currentValues = Array.isArray(modelOutput?.[modelId]) ? [...(modelOutput?.[modelId] || [])] : [];

      let nextValues: string[] = currentValues;
      if (action === 'REMOVE') {
        nextValues = currentValues.filter((v) => v !== oldInternalId);
      } else if (action === 'UPDATE') {
        nextValues = currentValues.map((v) => (v === oldInternalId ? (newInternalId as string) : v));
      } else if (action === 'ADD') {
        nextValues = currentValues.includes(newInternalId as string)
          ? currentValues
          : [...currentValues, newInternalId as string];
      } else if (action === 'VERIFY') {
        nextValues = currentValues;
      }

      updateMessage.mutate({
        integrationId,
        threadId,
        modelId,
        facetValues: nextValues,
        meta: {
          type: 'TAG',
          verified: action !== 'REMOVE',
          updates: [
            {
              action: action,
              oldValues: oldInternalId || '',
              newValues: newInternalId || '',
              timestamp: new Date().toISOString(),
              user: {
                id: user?.sub,
                name: user?.name,
                email: user?.email,
              },
            },
          ],
        },
      });
      onTagChange(action);
      mixpanel.track(`Tag ${action}`);
      setOpenModelId(null);
      setOpenPopoverKey(null);
      setSearchValue(''); // Clear search value when tag is selected
    }

    // Close popover when tag crud dialog is opened
    useEffect(() => {
      if (tagCrudOpen) {
        // Keep track of which model is currently open for the dialog
        // but close the popover
        setSearchValue('');
      }
    }, [tagCrudOpen]);

    const handleOpenTagCrud = (modelId: string, tagName: string) => {
      setInitialTagName(tagName);
      setOpenModelId(modelId);
      setTagCrudOpen(true);
      // No need to close the popover as the useEffect will handle that
    };

    const handleCloseTagCrud = (open: boolean) => {
      setTagCrudOpen(open);
      if (!open) {
        // Popover can be reopened after dialog closes
        setSearchValue('');
      }
    };

    if (!models) {
      return null;
    }

    return (
      <div className={cn('flex flex-wrap items-center gap-1 px-2 pt-1 pb-2', { 'pl-0': compact }, className)}>
        {sortMessageModels(Object.entries(modelOutput)).map(([modelId, output]) => {
          const model = modelMap.get(modelId);
          if (!model) {
            console.warn(`Model ${modelId} not found, tag: ${output?.toString() || ''}`);
            return null;
          }

          const uniqueOutput = Array.from(new Set(output || []));
          return uniqueOutput.map((tagInternalId) => {
            const tag = model.tags.get(tagInternalId);
            if (!tag) {
              console.warn(`Tag ${tagInternalId} not found, model: ${model.displayName}`);
              return null;
            }

            // Derive UI state from the tag status in the Models tab
            const isVerified = Boolean(facetsVersions?.[modelId] === 'actual');
            const isDeleted = tag.status === 'REMOVED' || tag.status === 'PENDING_REMOVED';
            if (isDeleted) {
              return null;
            }
            const isSuggested = tag.status === 'SUGGESTED';

            const popoverKey = `${modelId}:${tagInternalId}`;

            return (
              <Popover
                key={`${modelId}-${tagInternalId}`}
                open={openPopoverKey === popoverKey && !tagCrudOpen}
                onOpenChange={(open) => {
                  if (tagCrudOpen) return; // Don't change open state if tag crud is open
                  setOpenPopoverKey(open ? popoverKey : null);
                  if (!open) setSearchValue('');
                }}
              >
                <div
                  className={cn(
                    'flex items-center rounded-sm h-5 max-w-full',
                    isVerified && 'text-success-800 bg-success-500/25',
                    isSuggested && 'border border-neutral-200 bg-white',
                    !isVerified && !isSuggested && 'bg-neutral-200/50 group-data-[selected=true]:bg-white/20'
                  )}
                >
                  <PopoverTrigger
                    onClick={(e) => e.stopPropagation()}
                    id={`model-badge-${modelId}-${tagInternalId}`}
                    data-testid={`model-badge-${modelId}-${tagInternalId}`}
                    className={cn(
                      'flex items-center gap-1.5 text-xs h-full px-1.5 group/badge min-w-0 max-w-full',
                      isVerified && 'hover:bg-success-300/50 rounded-sm',
                      isSuggested && 'rounded-sm',
                      !isVerified && !isSuggested && 'hover:bg-neutral-200 rounded-l-sm'
                    )}
                  >
                    {isSuggested ? (
                      <WithTooltip
                        tooltip="This is a suggested tag. Click to confirm or select a different tag."
                        delay={500}
                      >
                        <Sparkles size={12} className="shrink-0 text-[#AD46FFBF]" />
                      </WithTooltip>
                    ) : (
                      <DynamicIcon
                        size={12}
                        name={getIconForModel({ id: modelId })}
                        className={cn('shrink-0', isVerified ? 'text-success-600' : 'text-dashboardblue')}
                      />
                    )}
                    <WithTooltip tooltip={tag?.shortName || output} delay={500} disabled={!tag?.shortName && !output}>
                      <span className="truncate min-w-0 block">{tag?.shortName || output}</span>
                    </WithTooltip>
                  </PopoverTrigger>
                  {!isVerified && !isSuggested && (
                    <WithTooltip tooltip="Mark as correct" delay={500}>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleVerify(modelId, tagInternalId);
                        }}
                        className="rounded-sm shrink-0 rounded-l-none h-full w-5 flex-center text-typography-grey/50 hover:bg-success hover:!text-white cursor-pointer"
                      >
                        <CheckCheck size={12} />
                      </div>
                    </WithTooltip>
                  )}
                  {Array.isArray(modelOutput?.[modelId]) && modelOutput?.[modelId]?.length > 1 && (
                    <WithTooltip tooltip="Remove this tag" delay={500}>
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChange(modelId, tagInternalId, null, 'REMOVE');
                        }}
                        className="rounded-sm shrink-0 rounded-l-none h-full w-5 flex-center text-typography-grey/50 hover:bg-destructive hover:!text-white cursor-pointer"
                      >
                        <X size={12} />
                      </div>
                    </WithTooltip>
                  )}
                </div>
                <RenderModelPopover
                  model={{
                    id: model.id,
                    displayName: model.displayName,
                    tags: Array.from(model.tags.values()),
                  }}
                  currentTagId={tagInternalId}
                  onChangeTag={(mId, currentTagId, newId, action) => handleChange(mId, currentTagId, newId, action)}
                  searchValue={searchValue}
                  setSearchValue={setSearchValue}
                  setTagCrudOpen={() => handleOpenTagCrud(modelId, searchValue)}
                  setInitialTagName={setInitialTagName}
                  canCreateNewTag={canCreateNewTag}
                />
              </Popover>
            );
          });
        })}
        {modelsForInbox.map((model) => {
          if (!modelOutput[model.id]) {
            return (
              <Popover
                key={model.id}
                open={openModelId === model.id && !tagCrudOpen}
                onOpenChange={(open) => {
                  if (tagCrudOpen) return; // Don't change open state if tag crud is open
                  setOpenModelId(open ? model.id : null);
                  if (!open) setSearchValue('');
                }}
              >
                <div className="flex items-center rounded-sm h-5 border border-dashed border-neutral-300">
                  <PopoverTrigger
                    onClick={(e) => e.stopPropagation()}
                    id={`model-badge-${model.id}-add`}
                    data-testid={`model-badge-${model.id}-add`}
                    className="flex items-center gap-1.5 text-xs whitespace-nowrap h-full px-1.5 group/badge hover:bg-neutral-50 rounded-sm text-neutral-500"
                  >
                    <Plus size={12} className="shrink-0 text-neutral-400" />
                    Add {model.displayName}
                  </PopoverTrigger>
                </div>
                <RenderModelPopover
                  model={{
                    id: model.id,
                    displayName: model.displayName,
                    tags: Array.from(model.tags.values()),
                  }}
                  onChangeTag={handleChange}
                  searchValue={searchValue}
                  setSearchValue={setSearchValue}
                  setTagCrudOpen={() => handleOpenTagCrud(model.id, searchValue)}
                  setInitialTagName={setInitialTagName}
                  canCreateNewTag={canCreateNewTag}
                />
              </Popover>
            );
          }
          return null;
        })}
        {canCreateNewTag && tagCrudOpen && openModelId && modelMap.get(openModelId) && (
          <WithAccessControl blockReaders>
            <TagCrudDialog
              model={{
                ...modelMap.get(openModelId)!,
                active: true,
                hidden: false,
                isNer: false,
                model: null,
                modelVersion: null,
                description: null,
                readonly: false,
                inboxes: {
                  all: true,
                  ids: [],
                },
                bookmarked: false,
                settings: {
                  integrations: [],
                  modelType: 'default',
                },
                tags: Array.from(modelMap.get(openModelId)!.tags.values()),
              }}
              open={tagCrudOpen}
              onOpenChange={handleCloseTagCrud}
              initialTagName={initialTagName}
              disableCreateMultiple={true}
            />
          </WithAccessControl>
        )}
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary rerenders
    return (
      prevProps.integrationId === nextProps.integrationId &&
      prevProps.threadId === nextProps.threadId &&
      JSON.stringify(prevProps.modelOutput) === JSON.stringify(nextProps.modelOutput) &&
      JSON.stringify(prevProps.facetsVersions) === JSON.stringify(nextProps.facetsVersions) &&
      prevProps.compact === nextProps.compact &&
      prevProps.className === nextProps.className &&
      prevProps.canCreateNewTag === nextProps.canCreateNewTag
    );
  }
);

ModelBadges.displayName = 'ModelBadges';

export default ModelBadges;
